import React, { useState, useEffect, useCallback } from "react";

import { useGetLLMPassThroughMutation } from "../../../store/api/lumi/llm";
import {
    useGetExactDocumentsMutation,
    useGetDocumentsMutation,
} from "../../../store/api/lumi/documents";
import SummaryContainer from "../../Summary/SummaryContainer";
import SummaryHeader from "../../Summary/SummaryHeader";
import SummaryDocuments from "../../Summary/SummaryDocuments";
import SummaryText from "../../Summary/SummaryText";

const NetSentimentChangeSummary = ({
    id,
    selectedConcept,
    filterBaseline,
    filterComparison,
}) => {
    const [triggerGetDocuments] = useGetDocumentsMutation();
    const [triggerGetLLMPassThrough] = useGetLLMPassThroughMutation();

    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [baselineDocuments, setBaselineDocuments] = useState([]);
    const [comparisonDocuments, setComparisonDocuments] = useState([]);

    const [loadingConceptSummary, setLoadingConceptSummary] = useState(false);
    const [baselineSummary, setBaselineSummary] = useState(null);
    const [comparisonSummary, setComparisonSummary] = useState(null);

    const fetchDocuments = useCallback(async () => {
        // nothing to do if we don't have a selectedConcept or breakdown information
        if (!selectedConcept?.texts.length) return;

        setLoadingDocuments(true);
        try {
            const [resultBaseline, resultComparison] = await Promise.all([
                triggerGetDocuments({
                    id,
                    filter: filterBaseline,
                    search: selectedConcept.texts,
                }).unwrap(),
                triggerGetDocuments({
                    id,
                    filter: filterComparison,
                    search: selectedConcept.texts,
                }).unwrap(),
            ]);

            setBaselineDocuments(resultBaseline.documents);
            setComparisonDocuments(resultComparison.documents);
        } catch (error) {
            console.error("Failed to fetch documents:", error);

            setBaselineDocuments([]);
            setComparisonDocuments([]);
        } finally {
            setLoadingDocuments(false);
        }
    }, [selectedConcept]);

    useEffect(() => {
        fetchDocuments();
    }, [selectedConcept]);

    const handleLlmRequest = useCallback(
        async (prompt) => {
            try {
                const llmResponse = await triggerGetLLMPassThrough({
                    projectId: id,
                    prompt: prompt,
                }).unwrap();

                return llmResponse.response;
            } catch (error) {
                console.error("failed to fetch summary", error);
                return null;
            }
        },
        [id, prompt]
    );

    const fetchSummary = useCallback(async () => {
        if (!selectedConcept) return;
        if (baselineDocuments.length === 0 || comparisonDocuments.length === 0)
            return;

        setLoadingConceptSummary(true);
        try {
            const baselinePrompt =
                `Summarize how "${selectedConcept.texts.join(", ")}" is spoken about in these documents\n` +
                baselineDocuments.map((d) => `<p>${d.text}</p>`).join("");

            const comparisonPrompt =
                `Summarize the difference in nature of mentions of "${selectedConcept.texts.join(", ")}", in between the
            the baseline document set and comparison document set given below. The two document sets
            are presented enclosed in appropriate tags:\n` +
                `<baseline-documents>\n` +
                baselineDocuments
                    .slice(0, 25)
                    .map((d) => `<p>${d.text}</p>`)
                    .join("") +
                `</baseline-documents>\n` +
                `<comparison-documents>\n` +
                comparisonDocuments
                    .slice(0, 25)
                    .map((d) => `<p>${d.text}</p>`)
                    .join("") +
                `</comparison-documents>\n`;

            const [baselineResult, comparisonResult] = await Promise.all([
                handleLlmRequest(baselinePrompt),
                handleLlmRequest(comparisonPrompt),
            ]);

            setBaselineSummary(baselineResult);
            setComparisonSummary(comparisonResult);
        } catch (error) {
            console.error("Failed to fetch summaries:", error);
        } finally {
            setLoadingConceptSummary(false);
        }
    }, [baselineDocuments, comparisonDocuments, handleLlmRequest]);

    useEffect(() => {
        fetchSummary();
    }, [baselineDocuments]);

    if (!selectedConcept)
        return (
            <SummaryContainer>
                <SummaryHeader>
                    Select a concept to show detailed summary.
                </SummaryHeader>
            </SummaryContainer>
        );

    return (
        <>
            <SummaryText
                header="Baseline Summary"
                loading={loadingConceptSummary}
                summary={baselineSummary}
            />
            <SummaryDocuments
                header={`Baseline Documents for ${selectedConcept.name}`}
                loading={loadingDocuments}
                documents={baselineDocuments}
            />
            <SummaryText
                header="Comparison Summary"
                loading={loadingConceptSummary}
                summary={comparisonSummary}
            />
            <SummaryDocuments
                header={`Comparison Documents for ${selectedConcept.name}`}
                loading={loadingDocuments}
                documents={comparisonDocuments}
            />
        </>
    );
};

export default NetSentimentChangeSummary;
